import { useState, useEffect } from 'react'
import { Search, User, X, Plus } from 'lucide-react'
import useInvoiceStore from '../../stores/useInvoiceStore'
import { customersAPI } from '../../services/api'

/**
 * Customer Selector Component
 * Search and select customer for invoice
 */
const CustomerSelector = ({ onAddNew }) => {
  const { selectedCustomer, setCustomer } = useInvoiceStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [customers, setCustomers] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // Search customers from API
  useEffect(() => {
    const searchCustomers = async () => {
      if (searchQuery.trim().length < 2) {
        setCustomers([])
        return
      }

      setIsLoading(true)
      try {
        const response = await customersAPI.search(searchQuery)
        setCustomers(response.data.data || [])
      } catch (error) {
        console.error('Error searching customers:', error)
        setCustomers([])
      } finally {
        setIsLoading(false)
      }
    }

    const debounceTimer = setTimeout(searchCustomers, 300)
    return () => clearTimeout(debounceTimer)
  }, [searchQuery])

  const handleSelectCustomer = (customer) => {
    setCustomer(customer)
    setSearchQuery('')
    setShowResults(false)
  }

  const handleClearCustomer = () => {
    setCustomer(null)
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        العميل *
      </h3>

      {!selectedCustomer ? (
        <div className="relative">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ابحث عن عميل..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setShowResults(true)
              }}
              onFocus={() => setShowResults(true)}
              className="input pr-10"
            />
          </div>

          {/* Results Dropdown */}
          {showResults && searchQuery && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-600 dark:text-gray-400">
                  <p>جاري البحث...</p>
                </div>
              ) : customers.length > 0 ? (
                customers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => handleSelectCustomer(customer)}
                    className="w-full text-right p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <p className="font-medium text-gray-900 dark:text-white">
                      {customer.title || customer.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400" dir="ltr">
                      {customer.phone || 'لا يوجد رقم'}
                    </p>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-600 dark:text-gray-400">
                  <p>لا توجد نتائج</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Selected Customer Card */
        <div className="card p-4 flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {selectedCustomer.title || selectedCustomer.name}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400" dir="ltr">
                {selectedCustomer.phone || 'لا يوجد رقم'}
              </p>
              {selectedCustomer.code && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  الكود: {selectedCustomer.code}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClearCustomer}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      )}

      {/* Add New Customer Button */}
      {!selectedCustomer && !searchQuery && (
        <button
          onClick={() => onAddNew && onAddNew()}
          className="w-full btn btn-secondary flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span>إضافة عميل جديد</span>
        </button>
      )}
    </div>
  )
}

export default CustomerSelector
